import { TableFooterContainer } from '@/global/components/DataGrid/components/DataTable/Footer'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { FormField } from '@/global/components/Form'
import ControlledTextField from '@/global/components/Form/Base/ControlledTextField'
import { SelectFuncionario } from '@/global/components/Selects'
import Grid from '@/global/components/ui/Grid'
import { Label } from '@/global/components/ui/label'
import ContentGuide from '@/global/guides/ContentGuide'
import Dialog from '@/global/guides/Dialog'
import { hasPermission } from '@/hooks/useBackendMenu'
import {
    formatFatorCompensacao,
    formatPorcentagemRegra,
    hoursToMinutes,
    minutesToHours,
} from '@acme/common'
import { useFormContext, useWatch } from 'react-hook-form'
import { z } from 'zod'

const HH_MM_REGEX = /^\d{1,3}:[0-5]\d$/

/** Máscara HH:mm (mesma da tela de abono) */
const MASCARA_HORA = {
    blocks: [2, 2],
    delimiters: [':'],
    numericOnly: true,
}

const justificativaSchema = z
    .string()
    .trim()
    .min(1, 'Insira uma justificativa')
    .max(190)

const horaObrigatoriaSchema = z
    .string()
    .regex(HH_MM_REGEX, 'Informe no formato HH:mm')

const horaSchema = horaObrigatoriaSchema.or(z.literal('')).optional()

/** Normaliza o valor digitado para HH:mm. */
function normalizarHora(raw: unknown) {
    const value = String(raw ?? '').trim()
    if (HH_MM_REGEX.test(value)) return value
    if (value.includes(':')) {
        const [hr, min] = value.split(':')
        return `${hr.padStart(2, '0')}:${min.padEnd(2, '0').slice(0, 2)}`
    }
    return `${value.padStart(2, '0')}:00`
}

interface IPropsHora {
    /** Título do modal, ex.: "Editar horas trabalhadas" */
    titulo: string
    /** Dia formatado, ex.: "ter 01/07" */
    diaLabel: string
    /** Valor atual do campo, ex.: "08:00" */
    valorAtual: string
    /** Justificativa da edição anterior do dia, para pré-preencher o campo */
    justificativaInicial?: string | null
    /** Complemento do placeholder da justificativa, ex.: "horas trabalhadas" */
    campoLabel: string
    onClose: () => void
    onSubmit: (input: {
        valor: string
        justificativa: string
    }) => Promise<unknown>
    isLoading: boolean
}

/**
 * Edição de um valor de hora único do dia (horas trabalhadas ou faltas),
 * com justificativa obrigatória.
 */
export function ModalHoraEdicao({
    titulo,
    diaLabel,
    valorAtual,
    justificativaInicial,
    campoLabel,
    onClose,
    onSubmit,
    isLoading,
}: IPropsHora) {
    return (
        <Dialog.Root
            open={hasPermission(['update', 'EdiçãoManualDoEspelhoDoPonto'])}
            loading={isLoading}
            onClose={onClose}
            schema={z.object({
                valor: horaObrigatoriaSchema,
                justificativa: justificativaSchema,
            })}
            defaultValues={{
                valor: valorAtual,
                justificativa: justificativaInicial ?? '',
            }}
            onSubmit={({ valor, justificativa }) =>
                onSubmit({ valor, justificativa })
            }
            height="auto"
        >
            <Dialog.Title>{titulo}</Dialog.Title>
            <Dialog.Content dividers>
                <Grid container spacing={4}>
                    <Grid item xs={6}>
                        <SelectFuncionario disabled size="medium" />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledTextField
                            variant="filled"
                            disabled
                            value={diaLabel}
                            label="Dia"
                        />
                    </Grid>
                    <Grid item xs={6} className="mt-2">
                        <ControlledTextField
                            variant="filled"
                            disabled
                            value={valorAtual}
                            label="Valor atual"
                        />
                    </Grid>
                    <Grid item xs={6} className="mt-2">
                        <FormField
                            name="valor"
                            label="Novo valor (hh:mm)"
                            required
                            autoFocus
                            InputLabelProps={{ shrink: true }}
                            placeholder="HH:mm"
                            options={MASCARA_HORA}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormField
                            name="justificativa"
                            label="Justificativa"
                            placeholder={`Descreva o motivo do ajuste de ${campoLabel}`}
                            rows={4}
                            multiline
                            min={1}
                            max={190}
                            required
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}

interface IPropsHorasExtras {
    /** Porcentagens da regra de cálculo do funcionário: ['50', '100', ...] */
    porcentagens: string[]
    /** Campo `compensacao` da regra por porcentagem: { '50': 50, '100': 100 } */
    compensacoes?: Record<string, number>
    /** Valores atuais por porcentagem, para pré-preencher: { '50': '01:00' } */
    valoresIniciais: Record<string, string>
    /** Justificativa da edição anterior do dia, para pré-preencher o campo */
    justificativaInicial?: string | null
    /** Dia formatado, ex.: "ter 01/07" */
    diaLabel: string
    onClose: () => void
    onSubmit: (input: {
        horasExtras: Record<string, string>
        justificativa: string
    }) => Promise<unknown>
    isLoading: boolean
}

/** Soma as horas extras digitadas e mostra o total (HH:mm) ao vivo. */
function TotalHorasExtras({ porcentagens }: { porcentagens: string[] }) {
    const valores = useWatch({ name: 'horasExtras' }) as
        | Record<string, string>
        | undefined
    const horas = porcentagens
        .map(porcentagem => valores?.[porcentagem])
        .filter((valor): valor is string => !!valor && HH_MM_REGEX.test(valor))
    const total = minutesToHours(hoursToMinutes(...horas))
    return <span className="font-semibold">{total}</span>
}

/**
 * Tabela editável das horas extras por porcentagem, sincronizada com o form do
 * Dialog. Cada célula "Quantidade" é editável (mesmo padrão do Pagamento de
 * Horas) e grava o valor normalizado em `horasExtras.<porcentagem>`.
 */
function TabelaHorasExtras({
    porcentagens,
    compensacoes = {},
}: {
    porcentagens: string[]
    compensacoes?: Record<string, number>
}) {
    const { setValue } = useFormContext()
    const valores =
        (useWatch({ name: 'horasExtras' }) as
            | Record<string, string>
            | undefined) ?? {}

    return (
        <ContentGuide.DataGrid
            loading={false}
            ToolbarProps={{ hideToolbar: true }}
            hideFooter
            className="rounded-sm border *:border-0"
            slots={{
                Footer: () => (
                    <TableFooterContainer className="*:flex *:justify-between bg-card">
                        <Label>Total de horas extras (bruto):</Label>
                        <TotalHorasExtras porcentagens={porcentagens} />
                    </TableFooterContainer>
                ),
            }}
            headerHeight={36}
            rowHeight={35}
            style={{ minHeight: 120 }}
            columns={[
                {
                    ...optionsColumn,
                    field: 'tipo',
                    align: 'left',
                    headerName: 'Tipo',
                    flex: 1,
                    valueFormatter: ({ value }) =>
                        `Horas extras ${formatPorcentagemRegra(value)}`,
                },
                {
                    ...optionsColumn,
                    field: 'fator',
                    headerName: 'Fator',
                    width: 130,
                    valueGetter: ({ row }) =>
                        formatFatorCompensacao(row.compensacao),
                },
                {
                    ...optionsColumn,
                    field: 'quantidade',
                    headerName: 'Quantidade',
                    width: 150,
                    editable: true,
                },
            ]}
            rows={porcentagens.map(porcentagem => ({
                id: porcentagem,
                tipo: porcentagem,
                compensacao: compensacoes[porcentagem] ?? 0,
                quantidade: valores[porcentagem] ?? '00:00',
            }))}
            onCellEditCommit={({ id, field, value }) => {
                if (field !== 'quantidade') return
                setValue(`horasExtras.${id}`, normalizarHora(value))
            }}
        />
    )
}

/**
 * Edição das horas extras do dia, distribuídas por porcentagem da regra de
 * cálculo do funcionário.
 */
export function ModalHorasExtrasEdicao({
    porcentagens,
    compensacoes,
    valoresIniciais,
    justificativaInicial,
    diaLabel,
    onClose,
    onSubmit,
    isLoading,
}: IPropsHorasExtras) {
    return (
        <Dialog.Root
            open={hasPermission(['update', 'EdiçãoManualDoEspelhoDoPonto'])}
            loading={isLoading}
            onClose={onClose}
            schema={z.object({
                horasExtras: z.record(horaSchema).default({}),
                justificativa: justificativaSchema,
            })}
            defaultValues={{
                horasExtras: Object.fromEntries(
                    porcentagens.map(porcentagem => [
                        porcentagem,
                        String(valoresIniciais[porcentagem] ?? '00:00'),
                    ]),
                ),
                justificativa: justificativaInicial ?? '',
            }}
            onSubmit={({ horasExtras = {}, justificativa }) =>
                onSubmit({
                    horasExtras: Object.fromEntries(
                        Object.entries(horasExtras).filter(
                            ([, valor]) => !!valor,
                        ),
                    ) as Record<string, string>,
                    justificativa,
                })
            }
            height="auto"
        >
            <Dialog.Title>Editar horas extras</Dialog.Title>
            <Dialog.Content dividers>
                <Grid container spacing={4}>
                    <Grid item xs={6}>
                        <SelectFuncionario disabled size="medium" />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledTextField
                            variant="filled"
                            disabled
                            value={diaLabel}
                            label="Dia"
                        />
                    </Grid>
                    <Grid item xs={12} className="mt-2">
                        <div className="flex flex-col gap-2">
                            <Label>Horas extras por tipo</Label>
                            <TabelaHorasExtras
                                porcentagens={porcentagens}
                                compensacoes={compensacoes}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <FormField
                            name="justificativa"
                            label="Justificativa"
                            placeholder="Descreva o motivo do ajuste das horas extras"
                            rows={4}
                            multiline
                            min={1}
                            max={190}
                            required
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
